import { useState } from 'react';
import { useFirebaseAuth } from '../../contexts/FirebaseAuthContext';

export const Paywall = () => {
    const { user, login, logout, sendEmailLink, signInWithEmail, emailLinkSent, emailLinkError, resetEmailLinkState, activateTrial, license, systemSettings } = useFirebaseAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginMode, setLoginMode] = useState<'link' | 'password'>('password');
    const [isLoading, setIsLoading] = useState(false);
    const [isActivating, setIsActivating] = useState(false);

    const handleAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim()) return;

        setIsLoading(true);
        try {
            if (loginMode === 'link') {
                await sendEmailLink(email.trim());
            } else {
                await signInWithEmail(email.trim(), password);
            }
        } catch (err) {
            // Error handled in context/toast
        } finally {
            setIsLoading(false);
        }
    };

    const handleActivateTrial = async () => {
        setIsActivating(true);
        await activateTrial();
        setIsActivating(false);
    };

    if (!user) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50 p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
                    <div className="text-center mb-8">
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Путевые Листы</h1>
                        <p className="text-gray-500">Приложение для управления путевыми листами</p>
                    </div>

                    {/* Google Sign-In */}
                    <button
                        onClick={login}
                        className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium transition mb-6 hover:bg-gray-50 hover:border-gray-400"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        Войти через Google
                    </button>

                    {/* Divider */}
                    <div className="flex items-center gap-4 mb-6">
                        <div className="flex-1 h-px bg-gray-200"></div>
                        <span className="text-sm text-gray-400">или</span>
                        <div className="flex-1 h-px bg-gray-200"></div>
                    </div>

                    {emailLinkSent ? (
                        <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                            <div className="text-green-600 mb-2">
                                <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="font-semibold text-green-800 mb-1">Проверьте вашу почту</h3>
                            <p className="text-sm text-green-600 mb-3">
                                Мы отправили ссылку для входа на <strong>{email}</strong>
                            </p>
                            <button
                                onClick={() => { resetEmailLinkState(); setEmail(''); }}
                                className="text-sm text-gray-500 underline hover:text-gray-700"
                            >
                                Использовать другой email
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleAuth}>
                            {/* Login Mode Toggle - Hidden as only Password is allowed now */}
                            {/* 
                        <div className="flex bg-gray-100 p-1 rounded-lg mb-4">
                            <button
                                type="button"
                                onClick={() => setLoginMode('password')}
                                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition ${loginMode === 'password' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                Пароль
                            </button>
                            <button
                                type="button"
                                onClick={() => setLoginMode('link')}
                                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition ${loginMode === 'link' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                Ссылка
                            </button>
                        </div>
                        */}

                            <div className="space-y-3 mb-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                                        required
                                    />
                                </div>

                                {loginMode === 'password' && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">
                                            Пароль
                                        </label>
                                        <input
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                                            required
                                        />
                                    </div>
                                )}
                            </div>

                            {emailLinkError && (
                                <p className="text-sm text-red-500 mb-3">{emailLinkError}</p>
                            )}

                            <button
                                type="submit"
                                disabled={isLoading || !email.trim() || (loginMode === 'password' && !password.trim())}
                                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed transition"
                            >
                                {isLoading ? 'Обработка...' : (loginMode === 'link' ? 'Отправить ссылку' : 'Войти')}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen flex flex-col items-center justify-center bg-gray-50 p-4 text-center">
            <h2 className="text-xl font-bold text-red-600 mb-2">Нет активной подписки</h2>
            <p className="text-gray-600 mb-6">Пользователь: {user.email}</p>

            {license ? (
                <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
                    <p className="font-medium">Срок действия лицензии истек</p>
                    <p className="text-sm mt-1">Свяжитесь с администратором для продления.</p>
                </div>
            ) : (
                <>
                    {systemSettings.trialModeEnabled ? (
                        <button
                            onClick={handleActivateTrial}
                            disabled={isActivating}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition mb-4 disabled:opacity-50"
                        >
                            {isActivating ? 'Активация...' : 'Начать пробный период'}
                        </button>
                    ) : (
                        <div className="mb-6 p-4 bg-gray-100 text-gray-600 rounded-lg border border-gray-200">
                            <p className="font-medium">Регистрация временно ограничена</p>
                            <p className="text-sm mt-1 text-gray-500">Автоматическая активация пробных периодов приостановлена администратором.</p>
                        </div>
                    )}
                </>
            )}

            <div>
                <button onClick={logout} className="text-gray-500 underline">Выйти</button>
            </div>
        </div>
    );
};
